import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const JIRA_BASE_URL = process.env.REACT_APP_JIRA_BASE_URL || 'https://distributedglobal.atlassian.net/browse';

const EXCLUDED_PREFIXES = /^(GPT|UTF|ISO|IEEE|HTTP|HTTPS|API|CPU|GPU|RAM|ROM|PDF|PNG|JPG|JPEG|GIF|SVG|XML|JSON|HTML|CSS|SQL|SSH|FTP|SMTP|DNS|TCP|UDP|SHA|MD5|RSA|AES|DES|JWT|AWS|GCP)-\d+$/i;

function isIssueId(text) {
  return /^[A-Z][A-Z0-9]{1,9}-\d+$/.test(text) && !EXCLUDED_PREFIXES.test(text);
}

function formatDates(text) {
  if (!text) return text;
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  return text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (match, year, month, day) => {
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    if (monthIndex >= 0 && monthIndex < 12 && dayNum >= 1 && dayNum <= 31) {
      return `${dayNum} ${monthNames[monthIndex]} ${year}`;
    }
    return match;
  });
}

function convertIssueIds(text, keyPrefix) {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split(/\b([A-Z][A-Z0-9]{1,9}-\d+)\b/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (isIssueId(part)) {
      return (
        <a key={`${keyPrefix}-${i}`} href={`${JIRA_BASE_URL}/${part}`} className="issue-link" target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return part;
  });
}

function parseItalicWithLinks(text, keyPrefix) {
  if (!text) return [];
  const result = [];
  let remaining = text;
  let partIndex = 0;
  while (remaining) {
    const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
    if (italicMatch) {
      const before = remaining.slice(0, italicMatch.index);
      if (before) {
        const linked = convertIssueIds(before, `${keyPrefix}-${partIndex++}`);
        if (Array.isArray(linked)) result.push(...linked);
        else result.push(linked);
      }
      result.push(
        <em key={`${keyPrefix}-i${partIndex++}`}>
          {convertIssueIds(italicMatch[1], `${keyPrefix}-il${partIndex}`)}
        </em>
      );
      remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
    } else {
      if (remaining) {
        const linked = convertIssueIds(remaining, `${keyPrefix}-${partIndex++}`);
        if (Array.isArray(linked)) result.push(...linked);
        else result.push(linked);
      }
      break;
    }
  }
  return result;
}

function formatMessageText(text) {
  if (!text) return text;
  let processedText = formatDates(text);
  const result = [];
  let remaining = processedText;
  let partIndex = 0;
  while (remaining) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const before = remaining.slice(0, boldMatch.index);
      if (before) {
        result.push(...parseItalicWithLinks(before, `p${partIndex++}`));
      }
      result.push(
        <strong key={`b${partIndex++}`}>
          {parseItalicWithLinks(boldMatch[1], `bi${partIndex}`)}
        </strong>
      );
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
    } else {
      result.push(...parseItalicWithLinks(remaining, `p${partIndex++}`));
      break;
    }
  }
  return result.length > 0 ? result : processedText;
}

export default function SharedThreadView({ token }) {
  const { getAuthHeader } = useAuth();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        const res = await fetch(`/api/share/${token}`, {
          headers: { Authorization: getAuthHeader() }
        });
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'Thread not found' : 'Failed to load thread');
        }
        const data = await res.json();
        if (data.success) {
          setThread(data.thread);
        } else {
          throw new Error('Failed to load thread');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchThread();
  }, [token, getAuthHeader]);

  if (loading) {
    return (
      <div className="shared-thread-loading">
        <p>Loading shared thread...</p>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="shared-thread-error">
        <p>{error || 'Thread not found'}</p>
        <a href="/">Back to app</a>
      </div>
    );
  }

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="shared-thread-container">
      <div className="shared-thread-header">
        <a href="/" className="shared-thread-back" title="Back to app">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
        </a>
        <div className="shared-thread-info">
          <div className="shared-thread-name">{thread.name}</div>
          <div className="shared-thread-meta">
            Shared by {thread.shared_by_name} {thread.shared_at ? `on ${formatTime(thread.shared_at)}` : ''} — Read-only
          </div>
        </div>
      </div>
      <div className="shared-thread-messages">
        <ul className="thread">
          {thread.messages.map((m, idx) => (
            <li key={idx} className={m.role === 'user' ? 'me' : 'bot'}>
              <span className="message-time">{formatTime(m.created_at)}</span>
              <span className="bubble">
                {m.role === 'assistant' ? formatMessageText(m.content) : m.content}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
